import { ACCOUNT_INFO, ACCOUNT_STATUS, EMPTY_STRING, LOCAL_STORAGE, OPERATION, SESSION_STORAGE, SVC_TYPE } from '@constants/common';
import { ADD_DETAIL } from '@constants/actions';
import { API_ENDPOINT } from '@constants/api';
import { SITE_URLS } from '@constants/routes';
import { sendPostRequestWithAuth, sendPostRequestWithState } from 'skyeye-fe-common-util';
import { getAccountState } from '@react/utils/auth.util';
import { UserManager } from 'oidc-client';
import { sessionTerminated, userFound } from 'redux-oidc';
import { NOTIFICATION } from '@localisation/message';
import { normalizeAccountFlags, toBoolean } from '@react/utils/accountFlags';
import createAction from './actionCreator';
import { operationInProgress, operationSuccess } from './common.actions';
import { getUser } from './user.actions';

export const mapAccountLoginToOidcUser = (accessToken: string, user: any): any => {
  const accountUser = user || {};
  const valueOrEmpty = (value: any) => value == null ? EMPTY_STRING : value;

  return {
    access_token: valueOrEmpty(accessToken),
    id_token: EMPTY_STRING,
    session_state: EMPTY_STRING,
    token_type: EMPTY_STRING,
    scope: EMPTY_STRING,
    expires_at: 0,
    state: EMPTY_STRING,
    expired: false,
    expires_in: 0,
    profile: {
      sub: valueOrEmpty(accountUser.Guid),
      user_token: valueOrEmpty(accessToken),
      userGuid: valueOrEmpty(accountUser.Guid),
      userName: valueOrEmpty(accountUser.UserName),
      firstName: valueOrEmpty(accountUser.FirstName),
      lastName: valueOrEmpty(accountUser.LastName),
      given_name: valueOrEmpty(accountUser.FirstName),
      family_name: valueOrEmpty(accountUser.LastName),
      preferred_username: valueOrEmpty(accountUser.UserName),
      name: [accountUser.FirstName, accountUser.LastName].filter(Boolean).join(' '),
      email: valueOrEmpty(accountUser.Email),
      contactNumber: valueOrEmpty(accountUser.ContactNumber),
      department: valueOrEmpty(accountUser.Department),
      isPublicUser: toBoolean(accountUser.IsPublicUser),
      isActive: toBoolean(accountUser.IsActive),
      deletedStatus: valueOrEmpty(accountUser.DeletedStatus)
    }
  };
};

export const logout = (history, oidcUserManager: UserManager) => {
  return async function (dispatch: any, getState: any) {
    try {
      await oidcUserManager?.removeUser();
    } catch (error) {
      // Finish local logout even if the OIDC user store cannot be cleared.
    }
    sessionStorage.removeItem(SESSION_STORAGE.KEYDEV_PERSISTED_FILTER);
    sessionStorage.removeItem(SESSION_STORAGE.OIDC_USER);
    localStorage.removeItem(LOCAL_STORAGE.ACCESS_TOKEN);
    localStorage.removeItem(LOCAL_STORAGE.USER_INFO);
    dispatch(createAction(ACCOUNT_STATUS.LOGGED_OUT));
    dispatch(sessionTerminated());
    history.push(SITE_URLS.LANDING);
  };
};

export const getAccountDetail = () => {
  return async function (dispatch: any, getState: any) {
    const response = await sendPostRequestWithState(API_ENDPOINT.ACCOUNT_INFO, {}, getState);
    return new Promise((resolve, reject) => {
      if (response && response.isSuccess) {
        const normalizedData = normalizeAccountFlags(response.data || {});
        dispatch(createAction(ADD_DETAIL, normalizedData));
        resolve(undefined);
      } else {
        reject(response);
      }
    });
  };
};

export const updateInfo = (newInfo) => {
  return async function (dispatch: any, getState: any) {
    const response = await sendPostRequestWithState(API_ENDPOINT.ACCOUNT_UPDATE, newInfo, getState);
    return new Promise((resolve, reject) => {
      if (response && response.isSuccess) {
        resolve(response);
      } else {
        dispatch(createAction(ACCOUNT_STATUS.CHANGED_FAIL, {}));
        reject(response);
      }
    });
  };
};

export const changePassword = (existPassword: string, newPassword: string, confirmPassword: string) => {
  return async function (dispatch: any, getState: any) {
    const response = await sendPostRequestWithState(API_ENDPOINT.CHANGE_PASSWORD, { existPassword, newPassword, confirmPassword }, getState);
    return new Promise((resolve, reject) => {
      if (response && response.isSuccess) {
        resolve(response);
      } else {
        reject(response);
      }
    });
  };
};

export const resetPassword = (resetType: string, resetKey: string, newPassword: string, confirmPassword: string) => {
  return async function (dispatch: any, getState: any) {
    const requestData = {
      resetType,
      resetKey,
      newPassword,
      confirmPassword
    };
    const response = await sendPostRequestWithState(API_ENDPOINT.RESET_PASSWORD, requestData, getState);
    return new Promise((resolve, reject) => {
      if (response && response.isSuccess) {
        resolve(response);
      } else {
        reject(response);
      }
    });
  };
};

export const forgetPassword = (email: string) => {
  return async function (dispatch: any, getState: any) {
    dispatch(operationInProgress(SVC_TYPE.ACCOUNT, OPERATION.UPDATE));
    const response = await sendPostRequestWithState(API_ENDPOINT.FORGET_PASSWORD, { email }, getState);
    return new Promise((resolve, reject) => {
      if (response && response.isSuccess) {
        dispatch(operationSuccess(NOTIFICATION.EMAIL_SENT_SUCCESS, SVC_TYPE.ACCOUNT, OPERATION.UPDATE));
        resolve(response);
      } else {
        dispatch(operationSuccess(NOTIFICATION.EMAIL_SENT_FAIL, SVC_TYPE.ACCOUNT, OPERATION.UPDATE));
        reject(response);
      }
    });
  };
};

export const oidcLogin = (oidcUserManager: UserManager) => {
  return async function (dispatch: any, getState: any) {
    oidcUserManager.signinRedirect();
  };
};

export const accountLogin = (userName: string, password: string) => {
  return async function (dispatch: any) {
    const response = await sendPostRequestWithState(API_ENDPOINT.LOGIN_ENDPOINT, { userName, password });
    return new Promise(async (resolve, reject) => {
      if (response && response.isSuccess) {
        const token = response.data.accessToken;
        localStorage.setItem(LOCAL_STORAGE.ACCESS_TOKEN, token);
        const accountInfoRes: any = await dispatch(getAccountInfo(token));
        if (accountInfoRes && accountInfoRes.isSuccess) {
          const fullAccountData = normalizeAccountFlags(accountInfoRes.data || {});
          const loginUser = response.data.user || fullAccountData;
          const oidcUser = mapAccountLoginToOidcUser(token, loginUser);
          localStorage.setItem(LOCAL_STORAGE.USER_INFO, JSON.stringify(fullAccountData));
          sessionStorage.setItem(SESSION_STORAGE.OIDC_USER, JSON.stringify(oidcUser));
          dispatch(userFound(oidcUser));
          dispatch(createAction(ACCOUNT_STATUS.LOGGED_IN, getAccountState(fullAccountData)));
          resolve(response);
        } else {
          reject(accountInfoRes);
        }
      } else {
        reject(response);
      }
    });
  };
};

export const getAccountInfo = (token) => {
  return async function (dispatch: any) {
    const response = await sendPostRequestWithAuth(API_ENDPOINT.ACCOUNT_INFO, {}, token);
    return new Promise((resolve, reject) => {
      if (response && response.isSuccess) {
        resolve(response);
      } else {
        reject(response);
      }
    });
  };
};

export const callback = (redirectUrl: string = SITE_URLS.MAIN, history?) => {
  return async function (dispatch: any, getState: any) {
    const oidcUser = getState().getIn(['Oidc', 'user']);
    const accountInfo = await dispatch(getAccountInfo(oidcUser.profile.user_token));
    dispatch(createAction(ACCOUNT_STATUS.LOGGED_IN, getAccountState(accountInfo.data)));
    if (history){
      history.push(decodeURIComponent(redirectUrl));
    }
  };
};

export const initAccountInfo = () => {
  return async function (dispatch: any, getState: any) {
    let oidcUser = getState().getIn(['Oidc', 'user']);
    if (!oidcUser && typeof window !== 'undefined') {
      try {
        const storedOidcUser = sessionStorage.getItem(SESSION_STORAGE.OIDC_USER);
        if (storedOidcUser) {
          dispatch(userFound(JSON.parse(storedOidcUser)));
          oidcUser = getState().getIn(['Oidc', 'user']);
        }
      } catch (error) {
        sessionStorage.removeItem(SESSION_STORAGE.OIDC_USER);
      }
    }
    if (oidcUser) {
      const accountInfo = await dispatch(getAccountInfo(oidcUser.profile.user_token));
      const normalizedAccountInfo = normalizeAccountFlags(accountInfo.data || {});
      const accountState = getAccountState(normalizedAccountInfo);
      dispatch(createAction(ACCOUNT_STATUS.LOGGED_IN, accountState));
    }
  };
};